import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import '../../../containers/PracticeManagementGroups/Conferences/Conferences.scss';
import '../../../containers/PracticeManagementGroups/Conferences/FinishedConferencesHost.scss';
import '../../../containers/PracticeManagementGroups/Conferences/VideoClipsList/VideoClipsList.scss';
import '../../../containers/PracticeManagementGroups/Conferences/VideoClipTile/VideoClipTile.scss';
import './EngagementMeetings.scss';
import Helpers from '../../../utils/helper';
import { EngagementMeeting } from '../../../slices/marketplace/meetings/models/get-engagement-conference';
import { EngagementMeetingStatus } from '../../../slices/marketplace/meetings/models/enums/meeting-status.enum';
import MeetingTile from '../../../components/MarketplaceMeetings/MeetingTile/MeetingTile';
import FinishedMeetingTile from '../../../components/MarketplaceMeetings/FinishedMeetingTile/FinishedMeetingTile';
import EngagementVideoClipsList from './EngagementVideoClipsList/EngagementVideoClipsList';
import { Button } from 'reactstrap';
import ScheduleVirtualMeetingModal from './ScheduleVirtualMeetingModal/ScheduleVirtualMeetingModal';
import { useCurrentMarketplaceParty } from '../../../custom-hooks/useCurrentMarketplaceParty';
import { TimelineParty } from '../../../slices/marketplace/timeline/models/enums/timeline-party.enum';
import { EngagementBuyer } from '../../../slices/marketplace/timeline/models/engagement-buyer.model';
import { MeetingScheduleType } from '../../../slices/marketplace/meetings/models/enums/meeting-schedule-type.enum';
import { useDispatch } from 'react-redux';
import { FetchEngagementConferences } from '../../../slices/marketplace/meetings/thunks/fetch-engagement-conferences.thunk';
import { GetTimelineById } from '../../../slices/marketplace/timeline/thunks/get-timeline-by-id.thunk';
import { useStateSelector } from '../../../store/selectors';
import createNotification from '../../../utils/createNotification';
import { ConferenceMeetingPopup } from '../../PracticeManagementGroups/Conferences/ConferenceMeetingPopup';
import VideoPlayerPopup from '../../VideoPlayerPopup/VideoPlayerPopup';
import {
    VideoClip,
    VideoClipPopup,
} from '../../PracticeManagementGroups/Conferences/SaveVideoClipPopup/SaveVideoClipPopup';
import {
    TeamMemberType,
    InternalPermissions,
    ExternalPermissions,
} from '../../Admin/BuyerMarketplace/MyTeamMembers/Models/Enums';
import { TimelineEvent } from '../../../slices/marketplace/timeline/models/timeline-event.model';
import { NotificationFlagsCategories } from '../../../models/NotificationFlags/NotificationFlagsCategories';
import { EngagementProcess } from '../../../slices/marketplace/timeline/models/engagement-process.model';
import { MeetingParticipantsType } from '../../../slices/marketplace/meetings/models/enums/meeting-participant-type.enum';

interface EngagementMeetingsProps {
    engagementId: number;
    meetingsGroupId?: number;
    engagementBuyers?: EngagementBuyer[];
    isBrokerExist: boolean;
    events?: TimelineEvent[];
    engagementProcess?: EngagementProcess;
    isSupportMode?: boolean;
    hideMeetingButtons?: boolean;
    customApiEndpoint?: string;
    selectedMeetingId?: number;
    notificationFlagPrefix?: string;
    isReadOnly?: boolean;
    isSfas?: boolean;
    participantTypes?: MeetingParticipantsType[];
}

export const EngagementMeetings = (props: EngagementMeetingsProps) => {
    const dispatch = useDispatch() as any;
    const party = useCurrentMarketplaceParty(props.engagementId);
    const isBroker = party === TimelineParty.Broker;
    const isAdmin = party === TimelineParty.VetValue;
    const [currentAndUpcomingMeetings, setCurrentAndUpcomingMeetings] =
        useState<EngagementMeeting[]>([]);
    const [pastMeetings, setPastMeetings] = useState<EngagementMeeting[]>([]);

    const allowedConferenceIds = useMemo(() => {
        if (!props.participantTypes) return undefined;
        return [...currentAndUpcomingMeetings, ...pastMeetings].map((m) => m.id);
    }, [props.participantTypes, currentAndUpcomingMeetings, pastMeetings]);
    const [isLoading, setIsLoading] = useState(false);
    const [loadingTrigger, setLoadingTrigger] = useState(0);
    const [isScheduleMeetingModalOpened, setIsScheduleMeetingModalOpened] =
        useState(false);
    const [meetingScheduleType, setMeetingScheduleType] =
        useState<MeetingScheduleType>(MeetingScheduleType.Scheduled);
    const engagementMeetingsState = useStateSelector(
        (state) => state.engagementMeetings
    );
    const axios = useStateSelector((state) => state.core.axios);
    const selectedOpportunity = useStateSelector(
        (state) => state.opportunities?.selectedOpportuity
    );
    const [selectedMeeting, setSelectedMeeting] = useState<EngagementMeeting>();
    const [
        isConferenceMeetingPopupVisible,
        setIsConferenceMeetingPopupVisible,
    ] = useState(false);
    const [videoSource, setVideoSource] = useState<string>();
    const [videoIsHls, setVideoIsHls] = useState<boolean>(false);
    const [videoClip, setVideoClip] = useState<VideoClip>();
    const [highlightedMeetingId, setHighlightedMeetingId] = useState<
        number | undefined
    >();
    const [processedSelectedMeetingId, setProcessedSelectedMeetingId] =
        useState<number | undefined>();
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        loadMeetings();
    }, []);

    useEffect(() => {
        if (
            !props.isSupportMode &&
            engagementMeetingsState?.engagementMeetings
        ) {
            const allMeetings = engagementMeetingsState.engagementMeetings;

            const meetingsToDisplay = props.participantTypes
                ? allMeetings.filter((m) =>
                      props.participantTypes!.includes(m.participantsType)
                  )
                : allMeetings;

            const filteredAndSortedMeetings =
                filterAndSortCurrentAndUpcomingMeetings(meetingsToDisplay);
            setCurrentAndUpcomingMeetings(filteredAndSortedMeetings);
            const filteredAndSortedCompletedMeetings =
                filterAndSortCompletedMeetings(meetingsToDisplay);
            setPastMeetings(filteredAndSortedCompletedMeetings);
        }
    }, [engagementMeetingsState?.engagementMeetings, props.isSupportMode, props.participantTypes]);

    useEffect(() => {
        if (
            props.selectedMeetingId &&
            (currentAndUpcomingMeetings.length > 0 || pastMeetings.length > 0)
        ) {
            setHighlightedMeetingId(props.selectedMeetingId);

            const selectedMeeting = [
                ...currentAndUpcomingMeetings,
                ...pastMeetings,
            ].find((m) => m.id === props.selectedMeetingId);

            const scrollTimer = setTimeout(() => {
                const highlightedElement = document.querySelector(
                    '.highlighted-meeting'
                );
                if (highlightedElement) {
                    highlightedElement.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center',
                        inline: 'center',
                    });
                }

                if (
                    selectedMeeting &&
                    props.selectedMeetingId !== processedSelectedMeetingId &&
                    !isConferenceMeetingPopupVisible &&
                    selectedMeeting.status !==
                        EngagementMeetingStatus.Completed &&
                    (selectedMeeting.status ===
                        EngagementMeetingStatus.InProgress ||
                        selectedMeeting.isInProgress ||
                        (selectedMeeting.dateStart &&
                            new Date(selectedMeeting.dateStart) <= new Date()))
                ) {
                    setProcessedSelectedMeetingId(props.selectedMeetingId);
                    handleJoinMeeting(selectedMeeting);
                }
            }, 500);

            const highlightTimer = setTimeout(() => {
                setHighlightedMeetingId(undefined);
            }, 10000);

            return () => {
                clearTimeout(scrollTimer);
                clearTimeout(highlightTimer);
            };
        }
    }, [props.selectedMeetingId, currentAndUpcomingMeetings, pastMeetings]);

    const filterAndSortCompletedMeetings = (
        meetings: EngagementMeeting[]
    ): EngagementMeeting[] => {
        const completedMeetings = meetings
            .filter(
                (m: EngagementMeeting) =>
                    m.status === EngagementMeetingStatus.Completed
            )
            .sort((a: EngagementMeeting, b: EngagementMeeting) => {
                const aDateEnd = a.dateEnd ? new Date(a.dateEnd).getTime() : 0;
                const bDateEnd = b.dateEnd ? new Date(b.dateEnd).getTime() : 0;
                return bDateEnd - aDateEnd;
            });
        return completedMeetings;
    };

    const filterAndSortCurrentAndUpcomingMeetings = (
        meetings: EngagementMeeting[]
    ): EngagementMeeting[] => {
        const filteredMeetings = meetings.filter(
            (m: EngagementMeeting) =>
                m.status !== EngagementMeetingStatus.Completed
        );

        const sortedMeetings = filteredMeetings.sort(
            (a: EngagementMeeting, b: EngagementMeeting) => {
                const aIsInProgress =
                    a.status === EngagementMeetingStatus.InProgress;
                const bIsInProgress =
                    b.status === EngagementMeetingStatus.InProgress;

                if (aIsInProgress && !bIsInProgress) {
                    return -1;
                }
                if (!aIsInProgress && bIsInProgress) {
                    return 1;
                }

                const aDateStart = a.dateStart
                    ? new Date(a.dateStart).getTime()
                    : 0;
                const bDateStart = b.dateStart
                    ? new Date(b.dateStart).getTime()
                    : 0;

                return aDateStart - bDateStart;
            }
        );

        return sortedMeetings;
    };

    const loadMeetings = async () => {
        setIsLoading(true);
        try {
            if (props.isSupportMode && props.customApiEndpoint) {
                const response = await axios.get(props.customApiEndpoint);
                if (response.status === 200) {
                    const meetings = response.data || [];
                    const meetingsToDisplay = props.participantTypes
                        ? meetings.filter((m: EngagementMeeting) =>
                              props.participantTypes!.includes(m.participantsType)
                          )
                        : meetings;
                    const filteredAndSortedMeetings =
                        filterAndSortCurrentAndUpcomingMeetings(meetingsToDisplay);
                    setCurrentAndUpcomingMeetings(filteredAndSortedMeetings);
                    const filteredAndSortedCompletedMeetings =
                        filterAndSortCompletedMeetings(meetingsToDisplay);
                    setPastMeetings(filteredAndSortedCompletedMeetings);
                }
            } else {
                // Refresh both meetings and timeline to get updated proposal data
                await Promise.all([
                    dispatch(FetchEngagementConferences(props.engagementId)),
                    dispatch(GetTimelineById({ id: props.engagementId, isTemplate: false }))
                ]);
            }
        } catch (error) {
            createNotification('Error loading meetings', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const hasEngagementMeetingsFunctionalAccess = (): boolean => {
        if (!selectedOpportunity) {
            return false;
        }

        if (isAdmin) {
            return true;
        }

        if (isBroker) {
            return true;
        }

        if (selectedOpportunity.isPrimaryBuyer) {
            return true;
        }

        if (party === TimelineParty.Seller) {
            return true;
        }

        if (selectedOpportunity.teamMemberType === TeamMemberType.Internal) {
            return (
                selectedOpportunity.internalPermissions ===
                    InternalPermissions.ReadOnlyAndCommunicate ||
                selectedOpportunity.internalPermissions ===
                    InternalPermissions.ReadOnlyAndCommunicateAndExecute
            );
        } else if (
            selectedOpportunity.teamMemberType === TeamMemberType.External
        ) {
            return (
                selectedOpportunity.externalPermissions ===
                    ExternalPermissions.VdrAndCommunicate ||
                selectedOpportunity.externalPermissions ===
                    ExternalPermissions.VdrAndReadOnlyCommunicate
            );
        }

        return false;
    };

    const isReadOnlyMode = () => !hasEngagementMeetingsFunctionalAccess() || !!props.isReadOnly;

    const handleJoinMeeting = (meeting: EngagementMeeting) => {
        setSelectedMeeting(meeting);
        setIsConferenceMeetingPopupVisible(true);
    };

    const scrollToMeetingIfNeeded = useCallback(() => {
        const scrollToMeetingId = sessionStorage.getItem('scrollToMeetingId');
        if (!scrollToMeetingId || currentAndUpcomingMeetings.length === 0) {
            return false;
        }

        const meetingId = Number(scrollToMeetingId);
        const meetingExists = currentAndUpcomingMeetings.some(
            (m) => m.id === meetingId
        );

        if (!meetingExists) {
            sessionStorage.removeItem('scrollToMeetingId');
            return false;
        }

        const meetingsSection = document.querySelector(
            '[data-section="current-and-upcoming-meetings"]'
        ) as HTMLElement;
        const meetingWrapper = document.querySelector(
            `[data-meeting-id="${meetingId}"]`
        ) as HTMLElement;

        if (meetingsSection && meetingWrapper) {
            const meetingElement = meetingWrapper.querySelector(
                '.engagement-meeting-tile'
            ) as HTMLElement;

            if (!meetingElement) {
                return false;
            }

            sessionStorage.removeItem('scrollToMeetingId');

            const rect = meetingElement.getBoundingClientRect();
            const isInViewport =
                rect.top >= 0 &&
                rect.left >= 0 &&
                rect.bottom <=
                    (window.innerHeight ||
                        document.documentElement.clientHeight) &&
                rect.right <=
                    (window.innerWidth || document.documentElement.clientWidth);

            if (!isInViewport) {
                meetingsSection.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start',
                });

                setTimeout(() => {
                    meetingElement.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center',
                    });
                }, 500);
            }

            meetingElement.classList.add('highlighted-meeting');

            const foundMeeting = currentAndUpcomingMeetings.find(
                (m) => m.id === meetingId
            );
            if (foundMeeting) {
                const isIn15Minutes = (date: Date) => {
                    let currentDate = new Date();
                    currentDate.setTime(currentDate.getTime() + 60 * 15 * 1000);
                    return new Date(date) <= currentDate;
                };

                const shouldShowJoinMeeting = () => {
                    if (isAdmin) {
                        const adminSupportTypes = [
                            MeetingParticipantsType.SellerAdminSupport,
                            MeetingParticipantsType.BuyerAdminSupport,
                            MeetingParticipantsType.AdvisorAdminSupport,
                        ];
                        return adminSupportTypes.includes(
                            foundMeeting.participantsType
                        );
                    }
                    return true;
                };

                if (
                    foundMeeting.dateStart &&
                    isIn15Minutes(foundMeeting.dateStart) &&
                    shouldShowJoinMeeting() &&
                    !isReadOnlyMode()
                ) {
                    handleJoinMeeting(foundMeeting);
                }
            }

            setTimeout(() => {
                meetingElement.classList.remove('highlighted-meeting');
            }, 3000);

            return true;
        }
        return false;
    }, [
        currentAndUpcomingMeetings,
        isAdmin,
        isReadOnlyMode,
        handleJoinMeeting,
    ]);

    useEffect(() => {
        if (currentAndUpcomingMeetings.length === 0) {
            return;
        }

        const attemptScroll = (attempts = 0) => {
            if (attempts > 40) {
                sessionStorage.removeItem('scrollToMeetingId');
                return;
            }

            if (
                containerRef.current &&
                containerRef.current.offsetParent !== null
            ) {
                if (scrollToMeetingIfNeeded()) {
                    return;
                }
            }

            setTimeout(() => attemptScroll(attempts + 1), 500);
        };

        setTimeout(() => attemptScroll(), 1000);
    }, [currentAndUpcomingMeetings, scrollToMeetingIfNeeded]);

    useEffect(() => {
        const handleFocus = () => {
            if (currentAndUpcomingMeetings.length > 0) {
                setTimeout(() => scrollToMeetingIfNeeded(), 500);
            }
        };

        window.addEventListener('focus', handleFocus);
        return () => window.removeEventListener('focus', handleFocus);
    }, [currentAndUpcomingMeetings, scrollToMeetingIfNeeded]);

    const handleViewRecording = (meeting: EngagementMeeting) => {
        setSelectedMeeting(meeting);
        fetchRecordingAndOpen(meeting.id);
    };

    const handleDownloadFile = (eventId: number, fileId: number) => {
        const meeting = [...currentAndUpcomingMeetings, ...pastMeetings].find(
            (m) => Number(m.id) === eventId
        );
        if (!meeting) {
            createNotification(
                'Unable to download file. Meeting information is missing.',
                'error'
            );
            return;
        }

        axios
            .get(
                `/api/calendars/${props.meetingsGroupId ?? meeting.commsId}/events/${eventId}/files/${fileId}`
            )
            .then((response: any) => {
                if (response.status === 200) {
                    window.open(response.data, '_blank');
                } else {
                    createNotification(
                        'Some unexpected error occurred while downloading file.',
                        'error'
                    );
                }
            })
            .catch(() => {
                createNotification(
                    'Some unexpected error occurred while downloading file.',
                    'error'
                );
            });
    };

    const isBuyerFlagFormat = props.notificationFlagPrefix?.includes(NotificationFlagsCategories.OpportunitiesCode);

    const getNotificationFlagCode = (meeting: EngagementMeeting): string => {
        if (isBuyerFlagFormat) {
            return `${props.notificationFlagPrefix}.${props.engagementId}.${NotificationFlagsCategories.CommsHubCode}.${NotificationFlagsCategories.MeetingsCode}.${meeting.id}.`;
        }
        return `${NotificationFlagsCategories.MpCode}.${props.engagementId}.${NotificationFlagsCategories.CommsHubCode}.${NotificationFlagsCategories.MeetingsCode}.${meeting.participantsType}.${meeting.id}.`;
    };

    const handleClipSave = () => {
        setLoadingTrigger(new Date().getTime());
    };

    const availableBuyersToScheduleMeeting = props.engagementBuyers
        ?.filter((b) => b.isActive)
        .map((b) => ({ id: b.buyerId, name: b.companyName }));

    const fetchRecordingLink = (meetingId: number) => {
        if (props.isSupportMode && !!props.customApiEndpoint) {
            return axios.get(
                `${props.customApiEndpoint}/${meetingId}/recording`
            );
        } else {
            return axios.get(
                `/api/Marketplace/Engagements/${props.engagementId}/EngagementMeetings/${meetingId}/recording`
            );
        }
    };

    const fetchRecordingAndOpen = (meetingId: number) => {
        fetchRecordingLink(meetingId)
            .then((response: any) => {
                if (response.status === 200) {
                    setVideoSource(response.data.url);
                    setVideoIsHls(response.data.isHls);
                } else {
                    createNotification(
                        'Error occured while getting Virtual Meeting Record',
                        'error'
                    );
                }
            })
            .catch(() => {
                createNotification(
                    'Error occured while loading Virtual Meeting Record',
                    'error'
                );
            });
    };

    return (
        <div className="engagement-meetings" ref={containerRef}>
            {(isAdmin || isBroker) && !props.hideMeetingButtons ? (
                <div className="meeting-buttons-wrapper">
                    <Button
                        className="btn btn-primary"
                        disabled={false}
                        onClick={() => {
                            setMeetingScheduleType(MeetingScheduleType.Instant);
                            setIsScheduleMeetingModalOpened(true);
                        }}>
                        Launch Instant Meeting
                    </Button>
                    <Button
                        className="btn btn-primary"
                        disabled={false}
                        onClick={() => {
                            setMeetingScheduleType(
                                MeetingScheduleType.Scheduled
                            );
                            setIsScheduleMeetingModalOpened(true);
                        }}>
                        Schedule Meeting
                    </Button>
                </div>
            ) : (
                <></>
            )}
            <div
                className="conferences"
                data-section="current-and-upcoming-meetings">
                <div className="header">
                    <label className="title">
                        Current and Upcoming Meetings
                    </label>
                </div>

                {isLoading || engagementMeetingsState?.isLoading ? (
                    Helpers.renderTableLoader()
                ) : (
                    <div className="conferences-wrapper">
                        {currentAndUpcomingMeetings?.length === 0 ? (
                            <span className="no-data-label">
                                No Data available
                            </span>
                        ) : (
                            []
                        )}
                        {currentAndUpcomingMeetings.map((meeting, index) => {
                            const isHighlighted =
                                highlightedMeetingId &&
                                meeting.id === highlightedMeetingId;
                            return (
                                <div
                                    key={'meeting-' + index}
                                    className={
                                        isHighlighted
                                            ? 'highlighted-meeting conference-box-wrapper'
                                            : 'conference-box-wrapper'
                                    }
                                    data-meeting-id={meeting.id}>
                                    <MeetingTile
                                        engagementId={props.engagementId}
                                        engagementProcess={props.engagementProcess}
                                        meeting={meeting}
                                        party={party}
                                        onJoinMeetingClick={handleJoinMeeting}
                                        onDownloadFile={handleDownloadFile}
                                        getNotificationFlagCode={
                                            getNotificationFlagCode
                                        }
                                        isReadOnlyMode={isReadOnlyMode()}
                                        isSfas={props.isSfas}/>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            <div className="conferences">
                <div className="header">
                    <label className="title">Past Virtual Meetings</label>
                </div>

                {isLoading ? (
                    Helpers.renderTableLoader()
                ) : (
                    <div className="conferences-wrapper">
                        {pastMeetings?.length === 0 ? (
                            <span className="no-data-label">
                                No Data available
                            </span>
                        ) : (
                            []
                        )}
                        {pastMeetings.map((meeting, index) => {
                            const isHighlighted =
                                highlightedMeetingId &&
                                meeting.id === highlightedMeetingId;
                            return (
                                <div
                                    key={'past-meeting-' + index}
                                    className={
                                        isHighlighted
                                            ? 'highlighted-meeting conference-box-wrapper'
                                            : 'conference-box-wrapper'
                                    }>
                                    <FinishedMeetingTile
                                        engagementId={props.engagementId}
                                        meeting={meeting}
                                        onViewRecording={handleViewRecording}
                                        onDownloadFile={handleDownloadFile}
                                        getNotificationFlagCode={
                                            getNotificationFlagCode
                                        }
                                        isReadOnlyMode={isReadOnlyMode()}/>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            <div className="conferences">
                <div className="header">
                    <label className="title">My Video Clips</label>
                </div>
                <EngagementVideoClipsList
                    engagementId={props.engagementId}
                    areMyClipsOnlyVisible={true}
                    loadingTrigger={loadingTrigger}
                    onClipSave={handleClipSave}
                    isReadOnlyMode={isReadOnlyMode()}
                    allowedConferenceIds={allowedConferenceIds}
                    customApiEndpoint={
                        props.isSupportMode && !!props.customApiEndpoint
                            ? `${props.customApiEndpoint}/clips`
                            : undefined
                    }
                />
            </div>

            <div className="conferences">
                <div className="header">
                    <label className="title">Shared Video Clips</label>
                </div>
                <EngagementVideoClipsList
                    engagementId={props.engagementId}
                    areMyClipsOnlyVisible={false}
                    loadingTrigger={loadingTrigger}
                    onClipSave={handleClipSave}
                    isReadOnlyMode={isReadOnlyMode()}
                    allowedConferenceIds={allowedConferenceIds}
                    customApiEndpoint={
                        props.isSupportMode && !!props.customApiEndpoint
                            ? `${props.customApiEndpoint}/clips/shared`
                            : undefined
                    }
                />
            </div>

            {isScheduleMeetingModalOpened && (
                <ScheduleVirtualMeetingModal
                    engagementId={props.engagementId}
                    onClose={() => setIsScheduleMeetingModalOpened(false)}
                    isAdmin={isAdmin}
                    isBroker={isBroker}
                    isBrokerExist={props.isBrokerExist}
                    availableBuyers={availableBuyersToScheduleMeeting}
                    meetingScheduleType={meetingScheduleType}
                />
            )}

            {isConferenceMeetingPopupVisible ? (
                <div className="conferences">
                    <ConferenceMeetingPopup
                        contextId={selectedMeeting.engagementId}
                        type={
                            props.isSupportMode && !selectedMeeting.engagementId
                                ? 'marketplaceSupport'
                                : 'marketplace'
                        }
                        isAdminBuyerSupport={selectedMeeting.participantsType === MeetingParticipantsType.BuyerAdminSupport}
                        conference={selectedMeeting}
                        onClose={() => {
                            setIsConferenceMeetingPopupVisible(false);
                            setTimeout(() => loadMeetings(), 2500);
                        }}/>
                </div>
            ) : (
                []
            )}

            {selectedMeeting && videoSource ? (
                <VideoPlayerPopup
                    title={selectedMeeting.name}
                    src={videoSource}
                    isHls={videoIsHls}
                    onClose={() => {
                        setVideoSource(undefined);
                    }}
                    isRecordingEnabled={true}
                    completeRecording={(startTime: number, endTime: number) => {
                        if (Math.abs(endTime - startTime) < 15) {
                            createNotification(
                                'Minimal Video Clip duration is 15 seconds. Please try again.',
                                'error'
                            );
                            return;
                        }
                        setVideoClip({
                            timeStartSeconds: startTime,
                            timeEndSeconds: endTime,
                            conferenceId: Number(selectedMeeting.id),
                        });
                    }}/>
            ) : (
                []
            )}

            {videoClip ? (
                <VideoClipPopup
                    clip={videoClip}
                    groupId={props.engagementId}
                    reset={() => setVideoClip(undefined)}
                    onClipSave={() => {
                        handleClipSave();
                    }}
                    type="marketplace"
                    isSupportMode={props.isSupportMode}/>
            ) : (
                []
            )}
        </div>
    );
};

export default EngagementMeetings;
